import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LinkButton from './LinkButton';
import './PromptBuilder.css';

// Prompt Builder: pick any pipeline prompt (active or legacy), fill its
// fields/checkboxes on the left, get the assembled SYSTEM/USER text on the
// right. Assembly happens on the server through the same code path the live
// pipeline uses (see /api/prompt-builder/* in server.js), so what you copy is
// exactly what the model would get. Empty required fields render as visible
// ⟨key⟩ markers — the preview is always live, never blocked by validation.

const STORAGE_KEY = 'promptBuilderValues';

async function api(path, opts) {
  const res = await fetch(`/api/prompt-builder${path}`, { credentials: 'include', ...opts });
  let data = null;
  try { data = await res.json(); } catch (e) { /* empty body */ }
  if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
  return data;
}

function loadStoredValues() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch (e) { return {}; }
}

const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* clipboard unavailable */ }
  };
  return (
    <button className="pb-copy-button" onClick={onCopy} disabled={!text}>
      {copied ? '✓ Copied' : label || '📋 Copy'}
    </button>
  );
};

const PromptBuilder = ({ onBack, backHref }) => {
  const [nodes, setNodes] = useState([]);
  const [nodeKey, setNodeKey] = useState('');
  // Field values are kept per prompt type, so switching types loses nothing.
  const [valuesByNode, setValuesByNode] = useState(loadStoredValues);
  const [result, setResult] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const requestSeq = useRef(0);

  const node = useMemo(() => nodes.find((n) => n.key === nodeKey) || null, [nodes, nodeKey]);
  const values = valuesByNode[nodeKey] || {};

  useEffect(() => {
    (async () => {
      try {
        const d = await api('/nodes');
        setNodes(d.nodes || []);
        if (d.nodes && d.nodes.length) setNodeKey((k) => k || d.nodes[0].key);
      } catch (e) { setError(e.message); }
    })();
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(valuesByNode)); } catch (e) { /* full/blocked */ }
  }, [valuesByNode]);

  // Debounced live render; a sequence counter drops answers that arrive out of
  // order (fast typing + slow network).
  const render = useCallback((key, vals) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const seq = ++requestSeq.current;
      setRendering(true);
      try {
        const d = await api('/render', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key, values: vals }),
        });
        if (seq === requestSeq.current) { setResult(d); setError(null); }
      } catch (e) {
        if (seq === requestSeq.current) setError(e.message);
      } finally {
        if (seq === requestSeq.current) setRendering(false);
      }
    }, 350);
  }, []);

  useEffect(() => {
    if (!nodeKey) return;
    render(nodeKey, valuesByNode[nodeKey] || {});
  }, [nodeKey, valuesByNode, render]);

  const setField = (fieldKey, value) => {
    setValuesByNode((prev) => ({ ...prev, [nodeKey]: { ...(prev[nodeKey] || {}), [fieldKey]: value } }));
  };

  const clearFields = () => {
    setValuesByNode((prev) => ({ ...prev, [nodeKey]: {} }));
  };

  // Dropdown groups: active prompts first, then the legacy ones, each split by
  // their pipeline area (Route / Points / Task).
  const grouped = useMemo(() => {
    const acc = new Map();
    for (const n of nodes) {
      const g = `${n.legacy ? 'Legacy' : 'Active'} · ${n.group}`;
      if (!acc.has(g)) acc.set(g, []);
      acc.get(g).push(n);
    }
    return [...acc.entries()].sort(([a], [b]) => {
      const la = a.startsWith('Legacy') ? 1 : 0;
      const lb = b.startsWith('Legacy') ? 1 : 0;
      return la - lb || a.localeCompare(b);
    });
  }, [nodes]);

  const visibleFields = (node ? node.fields : []).filter((f) => !f.showIf || !!values[f.showIf]);
  const missing = (result && result.missing) || [];
  const warnings = (result && result.warnings) || [];

  const renderField = (f) => {
    const isMissing = missing.includes(f.key);
    const required = f.required || (f.requiredIf && !!values[f.requiredIf]);
    if (f.type === 'checkbox') {
      return (
        <label key={f.key} className="pb-field pb-field-checkbox">
          <input
            type="checkbox"
            checked={!!values[f.key]}
            onChange={(e) => setField(f.key, e.target.checked)}
          />
          <span>{f.label}</span>
        </label>
      );
    }
    return (
      <div key={f.key} className={`pb-field ${isMissing ? 'pb-field-missing' : ''}`}>
        <label className="pb-field-label" htmlFor={`pb-${f.key}`}>
          {f.label}
          {required && <span className="pb-required" title="Required">*</span>}
          {isMissing && <span className="pb-missing-tag">⚠ empty</span>}
        </label>
        {f.type === 'text' ? (
          <input
            id={`pb-${f.key}`}
            type="text"
            value={values[f.key] || ''}
            onChange={(e) => setField(f.key, e.target.value)}
          />
        ) : (
          <textarea
            id={`pb-${f.key}`}
            rows={4}
            value={values[f.key] || ''}
            onChange={(e) => setField(f.key, e.target.value)}
          />
        )}
        {f.hint && <div className="pb-hint">{f.hint}</div>}
      </div>
    );
  };

  const block = (title, text) => (
    <div className="pb-block">
      <div className="pb-block-header">
        <span className="pb-block-title">{title}</span>
        <span className="pb-block-size">{text.length} chars</span>
        <CopyButton text={text} />
      </div>
      <pre className="pb-block-text">{text}</pre>
    </div>
  );

  return (
    <div className="pb-container">
      <div className="pb-header">
        <h1>🧾 Prompt Builder</h1>
        <LinkButton href={backHref} onClick={onBack} className="back-button">Back to Editor</LinkButton>
      </div>

      {error && <div className="pb-error">⚠ {error}</div>}

      <div className="pb-columns">
        <div className="pb-form">
          <div className="pb-field">
            <label className="pb-field-label" htmlFor="pb-node">Prompt type</label>
            <select id="pb-node" value={nodeKey} onChange={(e) => setNodeKey(e.target.value)}>
              {grouped.map(([g, list]) => (
                <optgroup key={g} label={g}>
                  {list.map((n) => (
                    <option key={n.key} value={n.key}>
                      {n.label}{n.version != null ? ` (v${n.version})` : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {node && (
              <div className="pb-node-meta">
                {node.legacy && <span className="pb-badge pb-badge-legacy">🗄 Legacy</span>}
                <span className="pb-badge">
                  {node.mode === 'ctx' ? '⚙ Assembled by pipeline code' : '✎ Plain template fill-in'}
                </span>
              </div>
            )}
          </div>

          {visibleFields.map(renderField)}

          {node && node.fields.length > 0 && (
            <button className="pb-clear-button" onClick={clearFields}>Clear fields</button>
          )}
          {node && node.fields.length === 0 && (
            <div className="pb-hint">This prompt has no placeholders — the text on the right is final.</div>
          )}
        </div>

        <div className="pb-preview">
          {(missing.length > 0 || warnings.length > 0) && (
            <div className="pb-notice">
              {missing.length > 0 && <div>⚠ Not filled yet: {missing.join(', ')} — shown as ⟨…⟩ in the text.</div>}
              {warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
            </div>
          )}
          {result && result.error && <div className="pb-error">⚠ Assembly failed: {result.error}</div>}
          <div className={`pb-blocks ${rendering ? 'pb-rendering' : ''}`}>
            {result && result.system && block('SYSTEM', result.system)}
            {result && (result.user || !result.system) && block(result.system ? 'USER' : 'PROMPT', result.user || '')}
            {!result && <div className="pb-placeholder">Pick a prompt type to see the assembled text.</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptBuilder;
